import messages from "./en.generated.json";

type Messages = typeof messages;
type Section = keyof Messages;
export type LeafKey = {
  [K in Section]: `${K & string}.${keyof Messages[K] & string}`;
}[Section];

type MessageForKey<K extends LeafKey> = K extends `${infer SectionName}.${infer MessageName}`
  ? SectionName extends Section
    ? MessageName extends keyof Messages[SectionName]
      ? Messages[SectionName][MessageName]
      : never
    : never
  : never;

type PlaceholderNames<Value extends string> = Value extends `${string}{${infer Name}}${infer Rest}`
  ? Name | PlaceholderNames<Rest>
  : never;

type InterpolationValues<K extends LeafKey> =
  MessageForKey<K> extends string
    ? [PlaceholderNames<MessageForKey<K>>] extends [never]
      ? never
      : Record<PlaceholderNames<MessageForKey<K>>, string | number>
    : never;

export function t<K extends LeafKey>(
  key: K,
  ...values: InterpolationValues<K> extends never
    ? [values?: Record<never, never>]
    : [values: InterpolationValues<K>]
): string {
  const [section, messageKey] = key.split(".");
  let value = (messages as Record<string, Record<string, string>>)[section]?.[messageKey] ?? key;
  for (const [name, replacement] of Object.entries(values[0] ?? {})) {
    value = value.replace(`{${name}}`, String(replacement));
  }
  return value;
}

export function replaceMessages(next: unknown) {
  if (!next || typeof next !== "object") return false;
  const candidate = next as Record<string, unknown>;
  const sections = Object.keys(messages) as Array<keyof Messages>;

  for (const section of sections) {
    const nextSection = candidate[section];
    if (
      !nextSection ||
      typeof nextSection !== "object" ||
      Object.keys(messages[section]).some(
        (key) => typeof (nextSection as Record<string, unknown>)[key] !== "string",
      )
    ) {
      return false;
    }
  }

  for (const section of sections) {
    const nextSection = candidate[section] as Record<string, string>;
    const currentSection = messages[section] as Record<string, string>;
    for (const key of Object.keys(currentSection)) {
      currentSection[key] = nextSection[key];
    }
  }
  return true;
}

export default messages;
